<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import api from "@/api";
import { openCustomToast } from "@/assets/js/helpers";

import LandingFooter from "@/components/landing/LandingFooter.vue";
import LandingHeader from "@/components/landing/LandingHeader.vue";
import HeroBgLight from "@/assets/images/HeroBgLight.png";
import HeroBgDark from "@/assets/images/HeroBgDark.png";
import { UserStore } from "@/store/userStore";

const userStore = UserStore();
const mobileMenuOpen = ref(false);
const landingPageRef = ref<HTMLElement | null>(null);
let revealObserver: IntersectionObserver | null = null;

type Action = {
  heading: string;
  text: string;
  href?: string;
  icon?: string;
};

const ActionCards: Action[] = [
  {
    heading: "Email us",
    text: "info@enonsolutions.com",
    href: "mailto:info@enonsolutions.com",
  },
  {
    heading: "Give us a call",
    text: "+38169751719",
    href: "tel:+38169751719",
  },
];

type ContactForm = {
  name: string;
  email: string;
  company: string;
  message: string;
};

const createInitialForm = (): ContactForm => ({
  name: "",
  email: "",
  company: "",
  message: "",
});

const form = ref<ContactForm>(createInitialForm());
const isSubmitting = ref(false);

const isValidEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const submitForm = async (): Promise<void> => {
  const payload: ContactForm = {
    name: form.value.name.trim(),
    email: form.value.email.trim(),
    company: form.value.company.trim(),
    message: form.value.message.trim(),
  };

  if (!payload.name || !payload.email || !payload.company || !payload.message) {
    openCustomToast("All contact form fields are required.", "error");
    return;
  }

  if (!isValidEmail(payload.email)) {
    openCustomToast("The email address is not valid.", "error");
    return;
  }

  isSubmitting.value = true;

  try {
    await api.contactForm(payload);
    form.value = createInitialForm();
    openCustomToast("Message sent.", "success");
  } catch (error: unknown) {
    console.error("Error submitting form:", error);
    openCustomToast(
      "There was an issue while submitting the message.",
      "error"
    );
  } finally {
    isSubmitting.value = false;
  }
};

const isScrolled = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 0;
};

const initializeScrollReveal = async () => {
  await nextTick();

  revealObserver?.disconnect();

  if (!landingPageRef.value) return;

  const revealElements =
    landingPageRef.value.querySelectorAll<HTMLElement>(".scroll-reveal");

  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add("is-visible");
        revealObserver?.unobserve(entry.target);
      });
    },
    {
      threshold: 0.2,
      rootMargin: "0px 0px -10% 0px",
    }
  );

  revealElements.forEach((element) => {
    revealObserver?.observe(element);
  });
};

watch(
  () => userStore.theme,
  (newVal) => {
    // eslint-disable-next-line no-storage/no-browser-storage
    localStorage.setItem("theme", newVal ? "light" : "dark");
    userStore.setTheme();
    window.addEventListener("scroll", handleScroll);
  }
);

onMounted(() => {
  // eslint-disable-next-line no-storage/no-browser-storage
  userStore.theme = localStorage.getItem("theme") === "light";
  userStore.setTheme();

  handleScroll();
  void initializeScrollReveal();
  window.addEventListener("scroll", handleScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", handleScroll);
  revealObserver?.disconnect();
});
</script>
<template>
  <div>
    <div class="bg">
      <img
        :src="userStore.theme ? HeroBgLight : HeroBgDark"
        alt="bg"
        class="hero-bg"
      />
      <div class="content">
        <LandingHeader></LandingHeader>
        <div class="contact-elements">
          <section class="left">
            <div class="text-container">
              <h1>Get in touch</h1>
              <p>
                Have questions or ready to transform your teams time management
                process?
              </p>
            </div>
            <div class="actions-wrapper">
              <div
                v-for="ActionCard in ActionCards"
                :key="ActionCard.heading"
                class="actions-container"
              >
                <a :href="ActionCard.href" class="action-card">
                  <div class="action-text-container">
                    <p class="action-heading">{{ ActionCard.heading }}</p>
                    <p class="action-text">{{ ActionCard.text }}</p>
                  </div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="{24}"
                    height="{24}"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="#2c75f4"
                      d="m16 8.4l-8.9 8.9q-.275.275-.7.275t-.7-.275t-.275-.7t.275-.7L14.6 7H7q-.425 0-.712-.288T6 6t.288-.712T7 5h10q.425 0 .713.288T18 6v10q0 .425-.288.713T17 17t-.712-.288T16 16z"
                    ></path>
                  </svg>
                </a>
              </div>
            </div>
          </section>
          <form @submit.prevent="submitForm">
            <div id="cf-form">
              <input
                id="cf-name"
                v-model="form.name"
                class="cf-field"
                type="text"
                placeholder="Name"
              />
              <input
                id="cf-email"
                v-model="form.email"
                class="cf-field"
                type="email"
                placeholder="Email"
              />
              <input
                id="cf-company"
                v-model="form.company"
                class="cf-field"
                type="text"
                placeholder="Company name"
              />
              <textarea
                id="cf-message"
                v-model="form.message"
                class="cf-field"
                placeholder="Your message"
              ></textarea>
              <button
                class="primary-filled-button"
                onclick="handleSubmit()"
                :disabled="isSubmitting"
              >
                {{ isSubmitting ? "Sending..." : "Submit" }}
              </button>
            </div>
          </form>
        </div>
        <LandingFooter></LandingFooter>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.bg {
  position: relative;
  overflow: hidden;
  background-color: var(--figma-color-background-bg-dark);
  font-family: "Archivo", Arial, Helvetica, sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.content {
  z-index: 2;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 0;
  width: 100%;
  height: auto;
  object-fit: cover;
  object-position: top center;
  transform: translateX(-50%);
  pointer-events: none;
}

a {
  display: inline-flex;
  padding: 0.7rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 30rem;
  font-size: var(--figma-font-size-xl, 1.5rem);
  font-style: normal;
  font-weight: var(--figma-font-weight-bold, 700);
  text-decoration: none;
}

button {
  display: inline-flex;
  padding: 0.7rem 1.25rem;
  justify-content: center;
  align-items: center;
  gap: 0.625rem;
  border-radius: 30rem;
  font-size: var(--figma-font-size-xl, 1.5rem);
  font-style: normal;
  font-weight: var(--figma-font-weight-bold, 700);
}

section {
  padding-block: var(--figma-space-5xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--figma-space-7xl);
  max-width: 1262px;
}

input {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}

.primary-filled-button {
  border: 2px solid var(--figma-color-button-primary-filled-stroke, #163f97);
  background: var(--figma-color-button-primary-filled-background, #1a52c2);

  /* button/primary-raised */
  box-shadow: 0 -2px 4px 0 var(
        --figma-color-button-primary-filled-shadow,
        #163f97
      ) inset,
    0 4px 4px 0 var(--figma-color-button-primary-filled-highlight, #1f63e0)
      inset,
    0 4px 8px -2px var(--figma-color-shadow-md-1, rgba(10, 13, 18, 0.1)),
    0 2px 4px -2px var(--figma-color-shadow-md-2, rgba(10, 13, 18, 0.06));

  color: var(--figma-color-button-primary-filled-text, #fcfdfe);
}

.primary-outlined-button {
  border: 2px solid var(--figma-color-button-primary-outlined-stroke, #163f97);
  background: var(--figma-color-button-primary-outlined-fill, #1a52c2);
  color: var(--figma-color-button-primary-outlined-text, #0a1d47);
}

h1 {
  font-size: var(--figma-typography-font-size-text-6xl);
  color: var(--figma-color-button-primary-outlined-text);
  line-height: 100%;
}

p {
  font-size: var(--figma-typography-font-size-text-xl);
  color: var(--figma-color-text-text-muted);
}

.contact-elements {
  width: 100%;
  max-width: 1262px;
  display: flex;
  gap: 2rem;
  margin-top: var(--figma-space2xl);
}

.action-card {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  .action-heading {
    color: var(--figma-color-text-text);
    font-size: var(--figma-typography-font-size-text-lg);
  }

  .action-text {
    color: var(--figma-color-text-text-muted);
    font-size: var(--figma-typography-font-size-text-base);
    font-weight: 400;
  }

  svg {
    height: 2rem;
    width: 2rem;
  }

  display: flex;
  padding: var(--figma-space-md);
  border: 1px solid var(--figma-color-border-card);
  border-radius: 1.5rem;
  align-items: flex-end;
}

.actions-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
}

.text-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.left {
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
}

form {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-block: var(--figma-space-5xl);
}

#cf-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1.4rem;
}

#cf-form .cf-field {
  width: 100%;
  padding: 1rem !important;
  border: 1px solid var(--figma-color-border-card);
  border-radius: 1rem;
  background: var(--figma-color-background-bg);
  color: var(--figma-color-text-text);
  font-family: "Archivo", Arial, Helvetica, sans-serif;
  font-size: var(--figma-typography-font-size-text-lg);
  font-weight: 400;
  line-height: 1.4;
}

.cf-field::placeholder,
#cf-message::placeholder,
#cf-message::-webkit-input-placeholder,
#cf-message::-moz-placeholder {
  color: var(--figma-color-text-text-muted) !important;
  -webkit-text-fill-color: var(--figma-color-text-text-muted);
  font-family: "Archivo", Arial, Helvetica, sans-serif;
  font-size: var(--figma-typography-font-size-text-lg);
  font-weight: 400;
  line-height: 1.4;
  opacity: 1;
}

#cf-message {
  height: 313px;
  min-height: 10rem;
  resize: vertical;
}

@media (max-width: 1400px) {
  section {
    padding-block: var(--figma-space-5xl);
    padding-inline: var(--figma-space-3xl);
  }

  h1 {
    font-size: var(--figma-typography-font-size-text-4xl);
  }

  p {
    font-size: var(--figma-typography-font-size-text-lg);
  }

  h2 {
    font-size: var(--figma-typography-font-size-text-3xl);
  }

  h3 {
    font-size: var(--figma-typography-font-size-text-xl);
  }

  form {
    padding-right: var(--figma-space-3xl);
  }
}

@media (max-width: 1100px) {
  .left {
    width: 70%;
  }
}

@media (max-width: 900px) {
  .contact-elements {
    display: flex;
    flex-direction: column;
    gap: var(--figma-space-3xl);
  }

  .left {
    width: 100%;
    gap: var(--figma-space-2xl);
    align-items: flex-start;
    padding-block: 0;
  }

  form {
    padding-inline: var(--figma-space-3xl);
    padding-block: 0;
  }
}

@media (max-width: 700px) {
  section {
    padding-block: var(--figma-space-4xl);
    padding-inline: var(--figma-space-3xl);
    gap: var(--figma-space-3xl);

    h1 {
      font-size: var(--figma-typography-font-size-text-3xl);
    }

    p {
      font-size: var(--figma-typography-font-size-text-base);
    }

    h2 {
      font-size: var(--figma-typography-font-size-text-2xl);
    }

    h3 {
      font-size: var(--figma-typography-font-size-text-lg);
    }
  }

  button,
  a {
    font-size: var(--figma-typography-font-size-text-lg);
    padding: 0.5rem 1rem;
  }
}

@media (max-width: 650px) {
  .left {
    padding-inline: var(--figma-space-xl);
  }

  form {
    padding-inline: var(--figma-space-xl);
  }
}

@media (max-width: 500px) {
  h1 {
    font-size: var(--figma-typography-font-size-text-2xl);
  }

  p {
    font-size: var(--figma-typography-font-size-text-base);
  }

  h2 {
    font-size: var(--figma-typography-font-size-text-xl);
  }

  h3 {
    font-size: var(--figma-typography-font-size-text-lg);
  }
}
</style>
